import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, Chip,
  TextField, Stack, Divider, Collapse,
} from '@mui/material';
import LockIcon     from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import AddIcon      from '@mui/icons-material/Add';
import SaveIcon     from '@mui/icons-material/Save';
import PasswordDialog from './PasswordDialog';
import ConfirmDialog  from './ConfirmDialog';
import { createMember, deleteMember, getGroupSettings, updateGroupSettings } from '../api';

function MemberManager({ members, activeGroup, groupPassword, onMembersUpdated }) {
  const [newMemberName, setNewMemberName] = useState('');
  const [message, setMessage]             = useState(null);
  const [loading, setLoading]             = useState(false);

  const [settings, setSettings]               = useState({ naam_rekeninghouder: '', iban_rekeningnummer: '' });
  const [settingsUnlocked, setSettingsUnlocked] = useState(false);
  const [settingsLoading, setSettingsLoading]   = useState(false);
  const [settingsSaved, setSettingsSaved]       = useState(false);
  const [unlockedPassword, setUnlockedPassword] = useState('');

  // Dialog state
  const [addPwdDialog, setAddPwdDialog]         = useState(false);
  const [unlockPwdDialog, setUnlockPwdDialog]   = useState(false);
  const [deleteDialog, setDeleteDialog]         = useState({ open: false, id: null, name: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setSettingsUnlocked(false); setSettingsSaved(false); setUnlockedPassword('');
  }, [activeGroup]);

  // ── Add member ───────────────────────────────────────────────────────────
  const handleAddMemberClick = (e) => {
    e.preventDefault();
    if (!newMemberName.trim()) { setMessage({ type: 'error', text: 'Voer een naam in' }); return; }
    setAddPwdDialog(true);
  };

  const handleAddPasswordConfirm = async (password) => {
    setAddPwdDialog(false);
    if (password !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    setLoading(true); setMessage(null);
    try {
      await createMember(newMemberName.trim(), activeGroup);
      setMessage({ type: 'success', text: 'Staflid toegevoegd!' });
      setNewMemberName('');
      onMembersUpdated();
    } catch (err) { setMessage({ type: 'error', text: 'Fout bij toevoegen: ' + err.message }); }
    finally { setLoading(false); }
  };

  // ── Delete member ────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    try {
      await deleteMember(deleteDialog.id);
      setMessage({ type: 'success', text: 'Staflid verwijderd!' });
      onMembersUpdated();
    } catch (err) { setMessage({ type: 'error', text: 'Fout bij verwijderen: ' + err.message }); }
    setDeleteDialog({ open: false, id: null, name: '' });
  };

  // ── Bank settings ────────────────────────────────────────────────────────
  const handleUnlockClick = () => setUnlockPwdDialog(true);

  const handleUnlockPasswordConfirm = async (password) => {
    setUnlockPwdDialog(false);
    if (password !== groupPassword) { setMessage({ type: 'error', text: 'Onjuist wachtwoord!' }); return; }
    try {
      const data = await getGroupSettings(activeGroup);
      setSettings(data);
      setUnlockedPassword(password);
      setSettingsUnlocked(true);
    } catch { setMessage({ type: 'error', text: 'Kon bankgegevens niet laden.' }); }
  };

  const handleSaveSettings = async (e) => {
    e.preventDefault();
    setSettingsLoading(true); setMessage(null);
    try {
      const updated = await updateGroupSettings(activeGroup, unlockedPassword, settings.naam_rekeninghouder, settings.iban_rekeningnummer);
      setSettings(updated);
      setSettingsSaved(true);
      setTimeout(() => setSettingsSaved(false), 3000);
    } catch (err) { setMessage({ type: 'error', text: 'Fout bij opslaan: ' + err.message }); }
    finally { setSettingsLoading(false); }
  };

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={700} mb={2}>Beheer — {activeGroup}</Typography>

      {message && (
        <Alert severity={message.type === 'error' ? 'error' : 'success'} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      {/* ── Members ───────────────────────────────────────────────────────── */}
      <Typography variant="h6" fontWeight={600} mb={1.5}>Stafleden</Typography>

      <Box component="form" onSubmit={handleAddMemberClick} mb={3}>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            size="small"
            label="Nieuw Staflid Toevoegen"
            placeholder="Naam van het staflid"
            value={newMemberName}
            onChange={e => setNewMemberName(e.target.value)}
            disabled={loading}
            sx={{ flex: '1 1 200px' }}
          />
          <Button type="submit" variant="contained" startIcon={<AddIcon />} disabled={loading} sx={{ flexShrink: 0 }}>
            {loading ? 'Bezig...' : 'Toevoegen'}
          </Button>
        </Stack>
      </Box>

      {members.filter(m => m.name !== 'Voorschot').length === 0 ? (
        <Typography variant="body2" color="text.disabled" mb={4} textAlign="center">Geen stafleden gevonden</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 4 }}>
          {members.filter(m => m.name !== 'Voorschot').map(member => (
            <Chip
              key={member.id}
              label={member.name}
              onDelete={() => setDeleteDialog({ open: true, id: member.id, name: member.name })}
              color="primary"
              variant="filled"
            />
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      {/* ── Bank settings (locked) ─────────────────────────────────────────── */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1.5} gap={1}>
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1, minWidth: 0 }}>
          Bankgegevens Penningmeester
        </Typography>
        {!settingsUnlocked ? (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<LockIcon />}
            onClick={handleUnlockClick}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Ontgrendelen
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            startIcon={<LockOpenIcon />}
            onClick={() => { setSettingsUnlocked(false); setUnlockedPassword(''); }}
            sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
          >
            Vergrendelen
          </Button>
        )}
      </Stack>

      {!settingsUnlocked ? (
        <Typography variant="body2" color="text.secondary">
          Klik op "Ontgrendelen" om de bankgegevens te bekijken of te wijzigen. Deze worden automatisch ingevuld bij elke export.
        </Typography>
      ) : (
        <Collapse in={settingsUnlocked}>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Deze gegevens worden automatisch ingevuld in het exportformulier.
          </Typography>
          <Box component="form" onSubmit={handleSaveSettings}>
            <Stack spacing={2}>
              <TextField
                fullWidth
                size="small"
                label="Naam rekeninghouder"
                placeholder="bijv. M.J.G Dohmen"
                value={settings.naam_rekeninghouder}
                onChange={e => setSettings(s => ({ ...s, naam_rekeninghouder: e.target.value }))}
              />
              <TextField
                fullWidth
                size="small"
                label="IBAN rekeningnummer"
                placeholder="bijv. NL49BUNQ2112562959"
                value={settings.iban_rekeningnummer}
                onChange={e => setSettings(s => ({ ...s, iban_rekeningnummer: e.target.value }))}
                inputProps={{ style: { fontFamily: 'monospace', letterSpacing: '0.05em' } }}
              />
              <Box>
                <Button
                  type="submit"
                  variant="contained"
                  startIcon={<SaveIcon />}
                  disabled={settingsLoading}
                  color={settingsSaved ? 'success' : 'primary'}
                >
                  {settingsLoading ? 'Opslaan...' : settingsSaved ? '✓ Opgeslagen' : 'Opslaan'}
                </Button>
              </Box>
            </Stack>
          </Box>
        </Collapse>
      )}

      {/* Dialogs */}
      <PasswordDialog
        open={addPwdDialog}
        onClose={() => setAddPwdDialog(false)}
        onConfirm={handleAddPasswordConfirm}
        title="Staflid toevoegen"
        description="Voer het wachtwoord in om een nieuw staflid toe te voegen."
      />

      <PasswordDialog
        open={unlockPwdDialog}
        onClose={() => setUnlockPwdDialog(false)}
        onConfirm={handleUnlockPasswordConfirm}
        title="Bankgegevens ontgrendelen"
        description="Voer het wachtwoord in om de bankgegevens te bekijken en te wijzigen."
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
        onConfirm={handleDeleteConfirm}
        title="Staflid verwijderen?"
        message={`Weet je zeker dat je ${deleteDialog.name} wilt verwijderen?`}
        confirmText="Verwijderen"
        confirmColor="error"
      />
    </Paper>
  );
}

export default MemberManager;
